/**
 * EthrelAlertBanner - Sistema de Alertas de Aplicação de Ethrel
 * 
 * Exibe alertas baseados no intervalo configurado pelo usuário:
 * - Alerta crítico (vencido)
 * - Alerta urgente (≤3 dias)
 * - Alerta de aviso (≤7 dias)
 * - Status OK (em dia)
 * 
 * Também permite configurar o intervalo de aplicação
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Dialog, Button, Input (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "../translations";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Info icon component - ícone de informação intuitivo
const HelpIcon = ({ tooltip, className = "" }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button className={`inline-flex items-center justify-center w-5 h-5 text-orange-600 hover:text-orange-800 transition-colors cursor-help ${className}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
        <p className="text-sm leading-relaxed">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const EthrelAlertBanner = ({ propertyId, onRefresh }) => {
  const { t } = useTranslation();
  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState({
    intervalo_dias: 30,
    alerta_dias_antes: 7
  });
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchAlertData = async () => {
    if (!propertyId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ethrel-alert/${propertyId}`);
      setAlertData(response.data);
      setConfigForm({
        intervalo_dias: response.data.config?.intervalo_dias || 30,
        alerta_dias_antes: response.data.config?.alerta_dias_antes || 7
      });
    } catch (err) {
      console.error("Error fetching ethrel alert:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlertData();
  }, [propertyId]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/ethrel-config`, {
        property_id: propertyId,
        intervalo_dias: parseInt(configForm.intervalo_dias),
        alerta_dias_antes: parseInt(configForm.alerta_dias_antes)
      });
      setShowConfig(false);
      fetchAlertData();
      if (onRefresh) onRefresh();
      toast.success(t('eaConfigSaved') || "Configuracao salva com sucesso!", {
        description: `${t('eaIntervalLabel') || 'Intervalo'}: ${configForm.intervalo_dias} dias | ${t('eaAlertAdvance') || 'Alerta'}: ${configForm.alerta_dias_antes} dias`,
        duration: 4000
      });
    } catch (err) {
      console.error("Error saving ethrel config:", err);
      alert("Erro ao salvar configuração: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Dialog JSX rendered inline to avoid re-mount on state changes
  const configDialogJSX = (
    <Dialog open={showConfig} onOpenChange={setShowConfig}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">⚡</span>
            {t('eaConfigTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('eaConfigureInterval')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="intervalo" className="flex items-center gap-1">
              📅 {t('eaIntervalDays')}
            </Label>
            <Input
              id="intervalo"
              type="number"
              min="1"
              max="365"
              value={configForm.intervalo_dias}
              onChange={(e) => setConfigForm({...configForm, intervalo_dias: e.target.value})}
              placeholder="30"
            />
            <p className="text-xs text-gray-500 mt-1">{t('eaIntervalHint')}</p>
          </div>
          
          <div>
            <Label htmlFor="alerta" className="flex items-center gap-1">
              🔔 {t('eaAlertAdvance')}
            </Label>
            <Input
              id="alerta"
              type="number"
              min="1"
              max="30"
              value={configForm.alerta_dias_antes}
              onChange={(e) => setConfigForm({...configForm, alerta_dias_antes: e.target.value})}
              placeholder="7"
            />
            <p className="text-xs text-gray-500 mt-1">{t('eaAlertAdvanceHint')}</p>
          </div>

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>📋 {t('eaPreviewSummary')}</strong> {configForm.alerta_dias_antes} {t('eaPreviewDaysBefore')} {configForm.intervalo_dias} {t('eaPreviewDaysSince')}
            </p>
          </div>

          {/* Alert levels explanation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">📊 {t('eaAlertLevels')}:</p>
            <div className="space-y-1 text-xs">
              <p className="flex items-center gap-2"><span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span> <strong>{t('eaCritical')}</strong></p>
              <p className="flex items-center gap-2"><span className="w-3 h-3 bg-orange-500 rounded-full"></span> <strong>{t('eaUrgent')}</strong></p>
              <p className="flex items-center gap-2"><span className="w-3 h-3 bg-amber-400 rounded-full"></span> <strong>{t('eaWarning')}</strong></p>
              <p className="flex items-center gap-2"><span className="w-3 h-3 bg-green-500 rounded-full"></span> <strong>{t('eaOk')}</strong></p>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => setShowConfig(false)} className="flex-1">
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSaveConfig} 
            disabled={saving}
            className="flex-1 bg-orange-600 hover:bg-orange-700"
          >
            {saving ? t('savingLabel') : t('eaSaveConfig')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (!alertData) {
    return null;
  }

  // Don't show if no Ethrel applications and no config
  if (!alertData.last_application && !alertData.config?.configured) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-2">
            <span className="text-2xl">⚡</span>
            <div>
              <p className="font-medium text-gray-700 flex items-center gap-1">
                {t('eaEthrelControl')}
                <HelpIcon tooltip={t('eaEthrelTooltip')} />
              </p>
              <p className="text-sm text-gray-500">{t('eaConfigureToReceive')}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowConfig(true)}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            ⚙️ {t('eaConfigure')}
          </Button>
        </div>
        
        {configDialogJSX}
      </div>
    );
  }

  // Alert banner based on level
  const getAlertStyles = () => {
    switch (alertData.alert_level) {
      case 'critical':
        return {
          bg: 'bg-red-100 border-red-500',
          text: 'text-red-800',
          icon: '🚨',
          animate: 'animate-pulse'
        };
      case 'urgent':
        return {
          bg: 'bg-orange-100 border-orange-500',
          text: 'text-orange-800',
          icon: '⚠️',
          animate: ''
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 border-amber-400',
          text: 'text-amber-800',
          icon: '⏰',
          animate: ''
        };
      default:
        return {
          bg: 'bg-green-50 border-green-300',
          text: 'text-green-800',
          icon: '✅',
          animate: ''
        };
    }
  };

  const styles = getAlertStyles();

  // Don't show if dismissed and not critical
  if (dismissed && alertData.alert_level !== 'critical') {
    return {configDialogJSX};
  }

  return (
    <>
      <div className={`${styles.bg} ${styles.animate} border-2 rounded-lg p-4 mb-4`}>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">{styles.icon}</span>
            <div>
              <p className={`font-bold ${styles.text}`}>
                {alertData.message}
              </p>
              {alertData.last_application && (
                <div className="mt-2 text-sm space-y-1">
                  <p className={styles.text}>
                    📅 {t('eaLastApplication')}: <strong>{new Date(alertData.last_application.date + 'T00:00:00').toLocaleDateString('pt-BR')}</strong>
                    {alertData.last_application.lote && ` - ${t('eaLot')}: ${alertData.last_application.lote}`}
                    {alertData.last_application.proporcao && ` - ${t('eaProportion')}: ${alertData.last_application.proporcao}`}
                  </p>
                  <p className="text-gray-600">
                    ⏱️ {alertData.days_since_last} {t('eaDaysSinceLastApp')}
                    {alertData.config && ` (${t('eaIntervalLabel')}: ${alertData.config.intervalo_dias} ${t('eaDaysRemaining').replace('restantes', '')})`}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowConfig(true)}
              className="border-gray-300 hover:bg-white"
            >
              ⚙️ {t('eaConfigure')}
            </Button>
            {alertData.alert_level && alertData.alert_level !== 'critical' && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setDismissed(true)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕ {t('eaDismiss')}
              </Button>
            )}
          </div>
        </div>

        {/* History (show on expand) */}
        {alertData.history && alertData.history.length > 1 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
              📜 {t('eaViewHistory')} ({alertData.history.length})
            </summary>
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
              {alertData.history.map((app, idx) => (
                <div key={idx} className="text-xs bg-white/50 rounded px-2 py-1">
                  <span className="font-medium">{new Date(app.date + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                  {app.talhao_lote_ethrel && <span> - {app.talhao_lote_ethrel}</span>}
                  {app.ethrel_proporcao && <span> ({app.ethrel_proporcao})</span>}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
      
      {configDialogJSX}
    </>
  );
};

export default EthrelAlertBanner;
